import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ApiError } from "../../lib/api";
import { login } from "../../lib/auth";

interface LocationState {
  from?: { pathname: string };
}

/** Same-origin absolute paths only — no `//evil.com` open redirects. */
function safeRedirect(raw: string | null): string | null {
  if (raw === null) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromState = (location.state ?? {}) as LocationState;
  // Router state (ProtectedRoute bounce) wins; then the ?redirect= the
  // session-expiry handler set; then the dashboard.
  const redirectTo =
    fromState.from?.pathname ??
    safeRedirect(searchParams.get("redirect")) ??
    "/dashboard";
  const sessionExpired = searchParams.get("expired") === "1";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail || err.problem.title);
      } else {
        setError("Sign-in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 px-6 py-12">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-neutral-900">Sign in</h1>
        <p className="text-sm text-neutral-600">
          Welcome back. Sign in to your workspace.
        </p>
      </header>

      {sessionExpired && (
        <p
          role="status"
          className="rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-700"
        >
          Session expired — please sign in again.
        </p>
      )}

      <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error !== null && (
          <p
            role="alert"
            className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" fullWidth loading={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </Button>

        <div className="text-center">
          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:underline"
          >
            Forgot your password?
          </Link>
        </div>
      </form>

      <p className="text-center text-sm text-neutral-600">
        New here?{" "}
        <Link to="/signup" className="text-primary-600 hover:underline">
          Create an account
        </Link>
      </p>
    </main>
  );
}
